import React, { useEffect, useState } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react/dist/iconify.js';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalDatagrid from '../../components/mui/DataGrid/GlobalDatagrid';
import FromAndToDatePicker from '../../components/mui/FromAndToDatePicker';
import ActionDetails from '../../components/mui/GlobalAnchor/ActionDetails';
import useResponsive from '../../Hooks/useResponsive';
import {
  getSkillxReferralLeads,
  getSkillxReferralLeadsSummary,
} from '../../redux/features/skillxSlice';
import { skillxReferralLeadsTableHead } from '../../components/datagrid/skillx/skillxReferralLeadsTableHead';

/**
 * IntervalSkillX — Referral Leads (sales view).
 *
 * Friends referred by SkillX learners from the Student Portal's "Refer & Earn"
 * screen, each row carrying the referrer so a counsellor knows whose name to
 * open the call with.
 *
 * READ-ONLY, DELIBERATELY. The lead is created by the Student Portal and worked
 * afterwards through the ERP's existing lead screens — this grid is a lens, not
 * a second place to edit the same row. There is no Add button for the same
 * reason: a referral without a referring learner is not a referral, and only
 * the student portal can supply one.
 *
 * Scope comes from the token: a Sales Executive sees the referrals routed to
 * them, a Sales Team Head sees their team's, the SkillX head sees all of it.
 */
const SkillxReferralLeads = () => {
  const dispatch = useDispatch();
  const mdDown = useResponsive('down', 'md');

  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [status, setStatus] = useState('');

  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { gridSize, pageNum, rowData, search } = paginationData;
  const { referralLeads } = useSelector((store) => store.skillx);
  const { data, meta, summary, loading } = referralLeads;

  const dispatchParams = { paginationData, fromDate, toDate, status };

  useEffect(() => {
    if (pageNum && gridSize) {
      dispatch(getSkillxReferralLeads(dispatchParams));
      dispatch(getSkillxReferralLeadsSummary({ fromDate, toDate }));
    }
  }, [gridSize, pageNum, search, fromDate, toDate, status]);

  return (
    <GlobalWrapper title="SkillX Referral Leads">
      {/* ---- the four figures, over everything in scope (not this page) ---- */}
      <Stack
        direction={mdDown ? 'column' : 'row'}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <SummaryTile
          icon="solar:users-group-rounded-bold-duotone"
          label="Total referrals"
          value={summary.total}
          color="#2F72B3"
        />
        <SummaryTile
          icon="solar:clock-circle-bold-duotone"
          label="Yet to be worked"
          value={summary.new}
          color="#9A6B00"
        />
        <SummaryTile
          icon="solar:check-circle-bold-duotone"
          label="Admitted"
          value={summary.admitted}
          color="#1E7E4F"
        />
        <SummaryTile
          icon="solar:graph-up-bold-duotone"
          label="Conversion"
          value={`${summary.conversionRate}%`}
          color="#7B4BC4"
        />
      </Stack>

      <Box sx={{ display: 'flex', flexDirection: mdDown ? 'column' : 'row', gap: 2 }}>
        <Box sx={{ flex: 0.5 }}>
          <FromAndToDatePicker
            fullWidth
            disabled={false}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
          />
        </Box>
      </Box>

      {/* Status pills. "In progress" is intentionally absent — the ERP records
          no contacted-but-not-admitted state for SkillX (admission creates the
          only forward signal there is), so a filter for it would always be
          empty. See the controller's deriveStatus. */}
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
        {[
          { key: '', label: 'All', count: summary.total },
          { key: 'SUBMITTED', label: 'New', count: summary.new },
          { key: 'ENROLLED', label: 'Admitted', count: summary.admitted },
          { key: 'CLOSED', label: 'Closed', count: summary.closed },
        ].map((f) => (
          <Chip
            key={f.key || 'all'}
            label={`${f.label} (${f.count ?? 0})`}
            onClick={() => setStatus(f.key)}
            color={status === f.key ? 'primary' : 'default'}
            variant={status === f.key ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600 }}
          />
        ))}
      </Stack>

      <Box mt={2}>
        <Paper elevation={3}>
          <GlobalDatagrid
            tableHead={skillxReferralLeadsTableHead}
            rows={data || []}
            meta={meta?.totalRecords}
            rowUniqueId="lead_id"
            loading={loading}
            rowClick={true}
            actionComponent={<ActionDetails val={rowData?.lead_name} />}
          />
        </Paper>
      </Box>

      {/* Stated rather than hidden: the status filter is applied to the page
          that came back, so a filtered page can be short while the count above
          still reflects every referral. Removing it would mean a wrong total. */}
      {status && (
        <Typography sx={{ mt: 1, fontSize: 12.5, color: 'text.secondary' }}>
          Showing {data?.length || 0} matching row(s) on this page. The figures above
          count every referral in range.
        </Typography>
      )}
    </GlobalWrapper>
  );
};

/** One figure in the strip above the grid. */
const SummaryTile = ({ icon, label, value, color }) => (
  <Paper
    elevation={2}
    sx={{
      flex: 1,
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      borderLeft: `4px solid ${color}`,
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        flexShrink: 0,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        background: `${color}18`,
      }}
    >
      <Icon icon={icon} width="24" height="24" />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }} noWrap title={label}>
        {label}
      </Typography>
    </Box>
  </Paper>
);

export default SkillxReferralLeads;
